import { useCallback, useState } from 'react';
import { HelpCircle } from 'lucide-react';

import {
  Button,
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Alert,
  AlertDescription,
} from '@design-system';
import { type D2CPaymentTab, D2C_PAYMENT_TAB_LABELS } from '@shared/guidedPlanningPreferences';
import { cn } from '@client/lib';

import { QuoteCard } from '@/features/guided-financial-planning/components/quote-card';
import { InlinePremiumSlider } from '@/features/guided-financial-planning/components/inline-premium-slider';
import { RequestHelpDialog } from '@/features/guided-financial-planning/components/request-help-dialog';
import {
  type GuidedPlanningQuote,
  getQuoteId,
  getPremiumAmount,
} from '@/features/guided-financial-planning/hooks';
import type { D2CPaymentTabData } from '@/features/guided-financial-planning/hooks/use-d2c-quotes-display';
import type { PerTabPremiumApi } from '@/features/guided-financial-planning/hooks/use-per-tab-premium-state';

interface D2CQuotesTabViewProps {
  tabs: D2CPaymentTabData[];
  defaultTab: D2CPaymentTab | undefined;
  isSessionComplete: boolean;
  hasSelectedQuote: boolean;
  isQuoteAdded: (quote: GuidedPlanningQuote) => boolean;
  onSelectQuote: (quote: GuidedPlanningQuote) => void;
  onRemoveQuote: (quote: GuidedPlanningQuote) => void;
  onViewUnderwritingDetails?: (productName: string, resultsText: string | null) => void;
  getUnderwritingResult: (productName: string, isJoint: boolean) => UnderwritingResult | undefined;
  perTabPremium: PerTabPremiumApi;
}

const SUPPORT_EMAIL = 'support@wealthassurancegroup.com';

function EmptyTabContent({ tab }: { tab: D2CPaymentTab }) {
  const label = D2C_PAYMENT_TAB_LABELS[tab].toLowerCase();

  return (
    <Empty className="bg-muted/50 text-wrap rounded-xl border border-dashed">
      <EmptyHeader className="max-w-2xl">
        <EmptyTitle className="text-gray-950">No {label} options available</EmptyTitle>
        <EmptyDescription className="text-foreground">
          We don&apos;t have any {label} options for you right now. Send an email describing what
          you&apos;re looking for to connect with a licensed agent, review your options, and receive
          quotes. Licensed support is provided through Wealth Assurance Group, a Waterlily company.
        </EmptyDescription>
        <div className="bg-muted mt-3 rounded-lg border p-4 text-center">
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Help finding insurance options`}
            className="text-primary text-lg font-semibold hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </EmptyHeader>
    </Empty>
  );
}

const INITIAL_VISIBLE_COUNT = 4;

export function D2CQuotesTabView({
  tabs,
  defaultTab,
  isSessionComplete,
  hasSelectedQuote,
  isQuoteAdded,
  onSelectQuote,
  onRemoveQuote,
  onViewUnderwritingDetails,
  getUnderwritingResult,
  perTabPremium,
}: D2CQuotesTabViewProps) {
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [expandedTabs, setExpandedTabs] = useState<Set<D2CPaymentTab>>(new Set());
  const activeDefault = defaultTab ?? 'monthly';
  const [activeTab, setActiveTab] = useState<D2CPaymentTab>(activeDefault);
  const activeTabPremiumState = perTabPremium.getTabState(activeTab);

  const toggleExpanded = useCallback((tab: D2CPaymentTab) => {
    setExpandedTabs(prev => {
      const next = new Set(prev);
      if (next.has(tab)) {
        next.delete(tab);
      } else {
        next.add(tab);
      }
      return next;
    });
  }, []);

  return (
    <div className="space-y-4">
      {!isSessionComplete && (
        <Alert
          variant="info"
          className="flex! items-start gap-6 rounded-xl p-3 md:items-center"
        >
          <div className="flex items-start gap-2">
            <HelpCircle className="size-4! md:size-4.5! translate-y-0.5! shrink-0" />
            <AlertDescription className="text-primary-dark font-medium md:text-base">
              Need help finding the right plan? Speak with a licensed agent to review options and
              get quotes.
            </AlertDescription>
          </div>
          <Button
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => setShowHelpDialog(true)}
          >
            Get Help
          </Button>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as D2CPaymentTab)}
      >
        <div className="flex flex-col flex-wrap items-center justify-between gap-4 md:flex-row">
          <TabsList className="w-full md:w-max">
            {tabs.map(({ tab, quotes }) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="gap-1.5"
              >
                {D2C_PAYMENT_TAB_LABELS[tab]}
              </TabsTrigger>
            ))}
          </TabsList>

          {activeTabPremiumState && !isSessionComplete && (
            <InlinePremiumSlider
              value={activeTabPremiumState.value}
              min={activeTabPremiumState.bounds.min}
              max={activeTabPremiumState.bounds.max}
              step={activeTabPremiumState.bounds.step}
              onValueChange={value => perTabPremium.setTabPremium(activeTab, value)}
            />
          )}
        </div>

        {tabs.map(({ tab, quotes: rawQuotes }) => {
          const tabPremiumState = perTabPremium.getTabState(tab);
          const adjustedQuotes = tabPremiumState ? perTabPremium.getAdjustedQuotes(tab) : rawQuotes;

          // Sort adjusted quotes: cheapest premium first, then highest ROI
          const sortedQuotes = [...adjustedQuotes].sort((a, b) => {
            const premiumDiff = getPremiumAmount(a) - getPremiumAmount(b);
            if (premiumDiff !== 0) return premiumDiff;
            return (b.roi ?? 0) - (a.roi ?? 0);
          });

          const isExpanded = expandedTabs.has(tab);
          const visibleQuotes = isExpanded
            ? sortedQuotes
            : sortedQuotes.slice(0, INITIAL_VISIBLE_COUNT);
          const hiddenCount = Math.max(0, sortedQuotes.length - INITIAL_VISIBLE_COUNT);

          return (
            <TabsContent
              key={tab}
              value={tab}
              className="mt-4"
            >
              {rawQuotes.length === 0 ? (
                <EmptyTabContent tab={tab} />
              ) : (
                <div className="space-y-4">
                  {sortedQuotes.length === 0 ? (
                    <Empty className="bg-muted rounded-xl border border-dashed">
                      <EmptyHeader>
                        <EmptyTitle className="text-gray-950">No options at this budget</EmptyTitle>
                        <EmptyDescription className="text-foreground">
                          Try increasing the budget slider to see more options.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    <>
                      <div
                        className={cn(
                          'grid gap-4',
                          visibleQuotes.length === 1
                            ? 'mx-auto max-w-md grid-cols-1'
                            : 'grid-cols-1 md:grid-cols-2',
                        )}
                      >
                        {visibleQuotes.map(quote => {
                          const isAdded = isQuoteAdded(quote);
                          const isBlockedBySelection = hasSelectedQuote && !isAdded;
                          const isJoint =
                            'policyType' in quote &&
                            (quote as PolicyQuote).policyType === 'Joint Life';
                          const underwritingResult = getUnderwritingResult(
                            quote.productName,
                            isJoint,
                          );

                          return (
                            <QuoteCard
                              key={getQuoteId(quote)}
                              quote={quote}
                              onSelect={onSelectQuote}
                              onRemove={onRemoveQuote}
                              isAlreadyAdded={isAdded}
                              disabled={isSessionComplete || isBlockedBySelection}
                              showSelectionLimitTooltip={isBlockedBySelection}
                              underwritingStatus={underwritingResult?.status}
                              onViewUnderwritingDetails={
                                underwritingResult && onViewUnderwritingDetails
                                  ? () =>
                                      onViewUnderwritingDetails(
                                        quote.productName,
                                        underwritingResult.resultsText,
                                      )
                                  : undefined
                              }
                            />
                          );
                        })}
                      </div>

                      {hiddenCount > 0 && (
                        <div className="flex justify-center">
                          <Button
                            variant="link"
                            onClick={() => toggleExpanded(tab)}
                          >
                            {isExpanded
                              ? 'Show less'
                              : `Show ${hiddenCount} more option${hiddenCount === 1 ? '' : 's'}`}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <RequestHelpDialog
        open={showHelpDialog}
        onOpenChange={setShowHelpDialog}
      />
    </div>
  );
}
